import { create } from 'zustand';
import { api } from '../services/apiService';
import type { VpsStatus, ContainerStatus, SyncStatus } from '../types/vps';

interface VpsState {
  status: VpsStatus | null;
  containers: ContainerStatus[];
  syncStatus: SyncStatus | null;
  logs: Record<string, string[]>;
  loading: boolean;
  syncTriggering: boolean;
  fetchStatus: () => Promise<void>;
  fetchContainers: () => Promise<void>;
  fetchSyncStatus: () => Promise<void>;
  fetchLogs: (type: string, lines?: number) => Promise<void>;
  triggerSync: () => Promise<void>;
}

export const useVpsStore = create<VpsState>((set, get) => ({
  status: null,
  containers: [],
  syncStatus: null,
  logs: {},
  loading: false,
  syncTriggering: false,

  fetchStatus: async () => {
    try {
      const status = await api.get<VpsStatus>('/vps/status');
      set({ status });
    } catch (err) {
      console.error('Failed to fetch VPS status:', err);
      set({ status: { connected: false, error: 'Failed to fetch status' } });
    }
  },

  fetchContainers: async () => {
    try {
      const containers = await api.get<ContainerStatus[]>('/vps/containers');
      set({ containers });
    } catch (err) {
      console.error('Failed to fetch containers:', err);
      set({ containers: [] });
    }
  },

  fetchSyncStatus: async () => {
    try {
      const syncStatus = await api.get<SyncStatus>('/vps/sync/status');
      set({ syncStatus });
    } catch (err) {
      console.error('Failed to fetch sync status:', err);
    }
  },

  fetchLogs: async (type: string, lines = 100) => {
    try {
      const data = await api.get<{ content: string }>(`/vps/sync/logs?type=${type}&lines=${lines}`);
      const parsed = data.content ? data.content.split('\n').filter(Boolean) : [];
      set({ logs: { ...get().logs, [type]: parsed } });
    } catch (err) {
      console.error(`Failed to fetch ${type} logs:`, err);
      set({ logs: { ...get().logs, [type]: ['Failed to load logs.'] } });
    }
  },

  triggerSync: async () => {
    set({ syncTriggering: true });
    try {
      await api.post<{ message: string }>('/vps/sync/trigger', {});
      await get().fetchSyncStatus();
    } catch (err) {
      console.error('Failed to trigger sync:', err);
    } finally {
      set({ syncTriggering: false });
    }
  },
}));
